import { ApiError } from '@/shared/services/baseApiClient';
import { appsApi } from '@/features/apps/shared/services/apps';
import { InstallAppViewModel } from '@/features/apps/shared/models';
import { botsApi } from './bots';
import { BotConfigurationViewModel } from '../models/botConfigurationViewModel';
import { InstallBotViewModel } from '../models/request/installBotViewModel';
import { messagingWorkspacesApi } from '@/messaging/messaging';
import { AddOrReplaceMessagingWorkspaceRequest } from '@/messaging/models/addOrReplaceMessagingWorkspaceRequest';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';

class BotAppService {

	// updates the bot, marking it as enabled
	// and updates the workspace, marking HasChatBotAppInstalled flag as true
	public enableBot(workspace: WorkspaceViewModel, bot: BotConfigurationViewModel) {
		return botsApi.update(workspace.id, bot)
			.then(() => {
				return this.updateWorkspace(workspace, true);
				// what can we do if updating workspace fails?
			});
	}

	// updates the bot, marking it as disabled
	// and updates the workspace, marking HasChatBotAppInstalled flag as false
	public disableBot(workspace: WorkspaceViewModel, bot: BotConfigurationViewModel) {
		return botsApi.update(workspace.id, bot)
			.then(() => {
				return this.updateWorkspace(workspace, false);
				// what can we do if updating workspace fails?
			});
	}

	// installs the 'Bots' domain app
	// and the 'Apps' domain app if it doesn't exist already
	public fullInstall(workspaceId: string) {
		return this.getInstalledAppId(workspaceId, 'connectbot')
			.then(installedAppId => {
				return this.configureConnectBot(workspaceId, installedAppId);
			});
	}

	public getDefaultConfiguration(): BotConfigurationViewModel {
		return {
			disabled: true,
			captureName: true,
			captureEmail: true,
			captureEnquiry: false,
			enquiryOptions: [],
			followUpActions: [],
			rules: []
		};
	}

	private updateWorkspace(workspace: WorkspaceViewModel, hasChatBotAppInstalled: boolean) {
		const request: AddOrReplaceMessagingWorkspaceRequest = {
			id: workspace.id,
			name: workspace.name,
			hasChatBotAppInstalled
		};
		return messagingWorkspacesApi.addOrReplace(request);
	}

	private configureConnectBot(workspaceId: string, installId: string) {
		const config = this.getDefaultConfiguration() as InstallBotViewModel;
		config.workspaceId = workspaceId;
		config.installId = installId;
		return botsApi.install(config);
	}

	/** Gets the installed app, or creates it if it doesn't exist */
	private getInstalledAppId(workspaceId: string, appId: string): Promise<string> {
		return appsApi.getInstalledApp(workspaceId, appId)
			.then(app => {
				// app exists, return its id
				return app.installedAppId;
			})
			.catch((error: ApiError) => {
				if (error.status === 404) {
					// app does not exist, we'll create it
					return this.installInstalledApp(workspaceId, appId)
						.then(app => {
							return app.installedAppId;
						});
				}
				// anything other than a 404 is unexpected
				throw new Error(`Encountered unexpected error: Http Status ${error.status}`);
			});
	}

	private installInstalledApp(workspaceId: string, appId: string) {
		const installRequest = { appId, published: true } as InstallAppViewModel;
		return appsApi.install(workspaceId, installRequest);
	}

}

export const botAppSerivce = new BotAppService();
